import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { IconBadge } from "./IconBadge";
import { Heading, Text } from "./Typography";

const SPACING = { none: "", sm: "mb-2", md: "mb-3", lg: "mb-4" } as const;

/** Props of `SectionHeader`; `spacing` is the gap to the content below (`md` by default), tones follow the surface. */
export type SectionHeaderProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  icon?: IconGlyph;
  /** Icon chip: `sm` (28px tile, 16px glyph, default) or `md` (32px tile, 24px glyph) for a form card header. */
  iconSize?: "sm" | "md";
  description?: ReactNode;
  action?: ReactNode;
  size?: "section" | "title";
  /** Size of the description: `sm` (default) or `base` for a card-sized header such as the item notes card. */
  descriptionSize?: "sm" | "base";
  level?: 2 | 3 | 4;
  spacing?: keyof typeof SPACING;
};

/** Title of a page section with an optional icon chip, description and trailing action. */
export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(function SectionHeader(
  { title, icon, iconSize = "sm", description, action, size = "section", descriptionSize = "sm", level = 2, spacing = "md", className, ...rest },
  ref,
) {
  return (
    <div {...rest} ref={ref} className={cn("flex items-center gap-3", SPACING[spacing], className)}>
      {icon && <IconBadge icon={icon} size={iconSize} glyphSize={iconSize === "md" ? "lg" : undefined} shape="rounded" />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Heading level={level} size={size}>
          {title}
        </Heading>
        {description && (
          <Text size={descriptionSize} tone="muted">
            {description}
          </Text>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
});
